import {
   Button,
   CircularProgress,
   Dialog,
   DialogActions,
   DialogContent,
   FormControl,
   FormHelperText,
   Grid,
   ThemeProvider,
   Tooltip,
   Typography
} from '@material-ui/core'
import { createTheme } from '@material-ui/core/styles'
import reject from 'lodash/reject'
import { useEffect, useMemo, useState } from 'react'
import Recaptcha from 'react-recaptcha'
import { withRouter } from 'react-router-dom'
import Warning from '@material-ui/icons/Warning'

import FormField from 'components/form/FormField'
import BackButton from '../../../commons/BackButton'
import UserNotRegistered, {
   AssociateConfirmationMessage,
   ConfirmDeleteAssociateSnackbar
} from '../UserNotRegistered'

import { deleteAssociate, getAssociate, getCollaborator, sendCollaboratorMail } from 'actions/associateAction'
import { validateInformationContributor } from 'actions/contributor_api/Validate'
import { validateInformationProvider } from 'actions/provider_api/validate'
import { useStateValue } from 'context/store'
import useForm from 'hooks/useForm'
import { VALUES } from 'utils/constants'
import themeRoot from '../../../../theme/theme'
import { validations } from './RegisterForm.validations'

import { style } from './styles'
import PortalModal, { usePortalModal } from 'components/commons/PortalModal'
import { openSnackBarAction } from 'context/reducers/openSnackbarReducer'
import DialogUI from 'components/commons/DialogUI'

const initialState = {
   registerType: '',
   legalNature: '',
   documentType: '',
   documentNumber: '',
   email: '',
   siteKey: ''
}

const REGISTERED_COLABORADOR_MESSAGE = email => `“Señor usuario hemos enviado un mensaje al correo electrónico ${email}, verifíquelo para continuar con el proceso.”`

const LEGAL = VALUES.LEGAL_NATURE_TYPE[1].value // 'L'
const CONTRIBUTOR = VALUES.ASSOCIATE_TYPE[0].value // 'AP'
const ASOCIADO = VALUES.ASSOCIATE_TYPE[1].value
const COLABORADOR = VALUES.ASSOCIATE_TYPE[2].value
const PROVIDER = VALUES.ASSOCIATE_TYPE[3].value // 'PROVEEDOR'
const LEGAL_PERSON = VALUES.PERSON_TYPE_PROVIDER[1].value // 23

const RegisterForm = props => {
   const theme = createTheme({
      ...themeRoot,
      overrides: {
         MuiFormHelperText: {
            root: {
               marginBottom: '-15px',
               color: 'red'
            }
         }
      }
   })

   const [loading, setLoading] = useState(false)
   const [, dispatch] = useStateValue()
   const [contributorModalMessage, setIsContributorModalMessage] = useState()
   const [openDialog, setOpenDialog] = useState(false)
   const [openDialogError, setOpenDialogError] = useState("")
   const [thirdParty, setThirdParty] = useState({})
   const [isTooltipOpen, setIsTooltipOpen] = useState(false)
   const [view, setView] = useState({
      isForm: true,
      isRegistered: false,
      isNotRegistered: false,
      isUpdated: false
   })

   const { form, createControlledComponent } = useForm({
      validations,
      defaultValues: { ...initialState }
   })
   const ControlledFormField = createControlledComponent(FormField)
   const [registerType, legalNature, documentType] = form.watch([
      'registerType',
      'legalNature',
      'documentType'
   ])
   const isContributorOptionSelected = registerType === CONTRIBUTOR
   const isLegalNaturePerson = legalNature === LEGAL
   const isProviderOptionSelected = registerType === PROVIDER
   const { isValid, isSubmitted } = form.formState

   const handleFocusTooltip = () => {
      if (isContributorOptionSelected && isLegalNaturePerson)
         setIsTooltipOpen(true)
   }

   const handleBlurTooltip = () => {
      isContributorOptionSelected &&
         isLegalNaturePerson &&
         setIsTooltipOpen(false)
   }

   // const goToUserUpdatedView = () =>
   //     setView({
   //         isForm: false,
   //         isRegistered: false,
   //         isNotRegistered: false,
   //         isUpdated: true
   //     })

   const query = new URLSearchParams(location.search)
   const isUpdate = () => {
      return query.get('update') === '1'
   }

   const isUpdateParam = isUpdate()
   const goToLogin = () => props.history.push('/')
   
   const contributorDocumentsOptions = useMemo(
      () =>
         isContributorOptionSelected && legalNature !== LEGAL
            ? reject(VALUES.DOCUMENTS_TYPES, ['label', 'NIT'])
            : VALUES.DOCUMENTS_TYPES,
      [isContributorOptionSelected, legalNature]
   )

   const providerDocumentsOptions = useMemo(
      () =>
         isProviderOptionSelected && legalNature !== LEGAL_PERSON
            ? reject(VALUES.DOCUMENTS_TYPES_PROVIDER, ['label', 'NIT'])
            : VALUES.DOCUMENTS_TYPES_PROVIDER,
      [isProviderOptionSelected, legalNature]
   )

   const verifyCallback = response =>
      form.setValue('siteKey', response, { shouldValidate: true })
   const expiredCallback = () =>
      form.setValue('siteKey', '', { shouldValidate: true })

   // const showIsNotRegisteredUser = () => {
   //     setLoading(false)
   //     setView({ isForm: false, isRegistered: false, isNotRegistered: true })
   // }

   const modalParams = message => ({
      icon: 'warning',
      message,
      hideCloseButton: true,
      confirmButtonText: 'Entendido'
   })

   const handleDeleteAssociate = async associate => {
      try {
         await deleteAssociate(associate.id)
         setOpenDialog(false)
      } catch (error) {
         setOpenDialog(false)
      }
   }

   const { params, onClose, setModal } = usePortalModal()
   
   const sendForm = async () => {
      setLoading(true)
      if (registerType === CONTRIBUTOR) {
         await validateInformationContributor({ ...form.getValues() })
            .then(async value =>
               setIsContributorModalMessage(value?.data?.message)
            )
            .catch(e => {
               setLoading(false)
               setOpenDialogError(e?.response?.data?.message)
               // dispatch(openSnackBarAction(e?.response?.data?.message))
            })
      } else if (registerType === PROVIDER) {
         await validateInformationProvider({ ...form.getValues() })
            .then(res => {
               res?.data?.status === 'CREATED'
                  ? setIsContributorModalMessage(res?.data?.message)
                  : dispatch(openSnackBarAction(res?.data?.message))
            })
            .catch(e => {
               setLoading(false)
               dispatch(openSnackBarAction(e?.response?.data?.message))
            })
      } else if ([ASOCIADO, COLABORADOR].includes(registerType)) {
         const { documentType, documentNumber, email } = form.getValues()
         try {
            // if (registerType === VALUES.ASSOCIATE_TYPE[1].value && isUpdate()) {
            //     form.setValue('registerType', 'ASOCIADO')
            //     form.setValue('email', 'actualizacion@mutualser.com.co')
            // }

            if( registerType == COLABORADOR) {
              await getCollaborator(documentType, documentNumber, email).then(async associateResponse => {
                     setThirdParty(associateResponse)
                     try {
                        await sendCollaboratorMail(documentNumber)
                        setModal({...modalParams(REGISTERED_COLABORADOR_MESSAGE(email)), onConfirm: goToLogin })
                     } catch (error) {
                        dispatch(openSnackBarAction(error?.response?.data?.mensaje || 'Ha sucedido un error inesperado'))
                     }
                  })
            }
            else {
               await getAssociate(documentType, documentNumber, registerType).then(associateResponse => {
                  setThirdParty(associateResponse)
               })
               setView({
                  isForm: false,
                  isRegistered: false,
                  isNotRegistered: false,
                  isUpdated: false
               })
            }
         } catch (error) {
            if (error.response?.status === 404) {
               // register collaborator with wrong email exception
               if(registerType === COLABORADOR && error?.response?.data?.mensaje != "Recurso no encontrado.") {
                  setLoading(false)
                  return setModal({...modalParams(error?.response?.data?.mensaje)})
               } 
               
                  if (isUpdate()) {
                  setThirdParty(null)
                  dispatch(openSnackBarAction('Estimado usuario, no se encuentra habilitado para la actualización de sus datos, por favor escalar su caso a su gestor comunitario.'))
                  setView({
                     isForm: true,
                     isRegistered: false,
                     isNotRegistered: false,
                     isUpdated: false
                  })
               } else {
                  setThirdParty({
                     numeroIdentificacion: documentNumber,
                     tipoIdentificacion: documentType,
                     rol: registerType
                  })
                  setView({
                     isForm: false,
                     isRegistered: false,
                     isNotRegistered: false,
                     isUpdated: false
                  })
               }
            } else {
               setThirdParty(null)
               dispatch(openSnackBarAction(error?.response?.data?.mensaje || 'Ha sucedido un error inesperado'))
               setView({
                  isForm: true,
                  isRegistered: false,
                  isNotRegistered: false,
                  isUpdated: false
               })
            }
         }
      }
      setLoading(false)
   }

   const setFormFieldValue = (fieldName, value = '') =>
      form.setValue(fieldName, value, { shouldValidate: isSubmitted })

   const onRegisterTypeChange = async e => {
      if(e.target.value === PROVIDER){
         setModal(
            modalParams('Sr. Proveedor si usted va a realizar un trámite de debida diligencia para la vinculación o actualización de datos como proveedor administrativo o prestador de servicios de salud, diligenciar esta información.  '))
      }
      ;(e.target.value === CONTRIBUTOR || e.target.value === PROVIDER) &&
         setFormFieldValue('legalNature')
      isSubmitted && (await form.trigger())
   }

   const onLegalNatureChange = async e => {
      e.target?.value === LEGAL
         ? setFormFieldValue('documentType', 'NI')
         : setFormFieldValue('documentType', '')
   }

   const onPersonTypeChange = async e => {
      e.target?.value === LEGAL_PERSON
         ? setFormFieldValue('documentType', 12)
         : setFormFieldValue('documentType')
   }

   const handleCloseContributorModal = reason => {
      if (reason.type === 'click') {
         setIsContributorModalMessage('')
         goToLogin()
      }
   }

   useEffect(() => {
      if (isUpdateParam) {
         form.setValue('registerType', 'ASOCIADO')
         form.setValue('email', 'actualizacion@mutualser.com.co')
      }
   }, [isUpdateParam])

   useEffect(() => {
      isContributorOptionSelected &&
         isLegalNaturePerson &&
         setIsTooltipOpen(true)
   }, [isContributorOptionSelected, isLegalNaturePerson])

   return (
      <ThemeProvider theme={theme}>
         <PortalModal params={params} onClose={onClose} />
         {openDialog ? (
            <ConfirmDeleteAssociateSnackbar
               handleCancel={() => goToLogin()}
               handleAccept={async () => {
                  await handleDeleteAssociate(thirdParty)
                  goToLogin()
               }}
            />
         ) : (
            <>
               <Grid container spacing={0} style={style.container}>
                  <Grid item style={style.gridItem}>
                     <div style={style.paper}>
                        {!isUpdate() && <BackButton to="/auth/login" />}
                        {view.isForm ? (
                           <div style={style.containerForm}>
                              <Typography style={style.titleOne}>
                                 {isUpdate()
                                    ? 'Formulario de Actualización de Datos'
                                    : 'Formulario Solicitud Registro de Terceros'}
                              </Typography>
                              <div style={style.form}>
                                 <div>
                                    {!isUpdate() && (
                                       <ControlledFormField
                                          type="select"
                                          fieldName="registerType"
                                          fieldLabel="Tipo de Tercero"
                                          items={VALUES.ASSOCIATE_TYPE}
                                          onChange={onRegisterTypeChange}
                                          required
                                       />
                                    )}
                                    {(isContributorOptionSelected ||
                                       isProviderOptionSelected) && (
                                       <ControlledFormField
                                          type="select"
                                          fieldName="legalNature"
                                          fieldLabel="Tipo de persona"
                                          items={
                                             isProviderOptionSelected
                                                ? VALUES.PERSON_TYPE_PROVIDER
                                                : VALUES.LEGAL_NATURE_TYPE
                                          }
                                          required={
                                             isContributorOptionSelected ||
                                             isProviderOptionSelected
                                          }
                                          onChange={
                                             isProviderOptionSelected
                                                ? onPersonTypeChange
                                                : onLegalNatureChange
                                          }
                                       />
                                    )}
                                    <ControlledFormField
                                       type="select"
                                       fieldName="documentType"
                                       fieldLabel="Tipo de Documento"
                                       items={
                                          isProviderOptionSelected
                                             ? providerDocumentsOptions
                                             : contributorDocumentsOptions
                                       }
                                       required
                                       disabled={Boolean(
                                          (isContributorOptionSelected ||
                                             isProviderOptionSelected) &&
                                             (isLegalNaturePerson ||
                                                legalNature === LEGAL_PERSON)
                                       )}
                                    />
                                    <ControlledFormField
                                       style={{...style.formControl, marginBottom: 15}}
                                       type="idNumber"
                                       fieldName="documentNumber"
                                       fieldLabel="Número de identificación"
                                       documentType={documentType}
                                       size="small"
                                       required
                                    />
                                    <ControlledFormField
                                       key="email-field"
                                       type="email"
                                       style={{
                                          ...style.formControl,
                                          marginTop:
                                             isContributorOptionSelected &&
                                             isLegalNaturePerson &&
                                             isTooltipOpen
                                                ? 30
                                                : 5
                                       }}
                                       fieldName="email"
                                       fieldLabel="Correo Electrónico"
                                       size="small"
                                       onFocus={handleFocusTooltip}
                                       onBlur={handleBlurTooltip}
                                       internalErrorMessage={false}
                                       required
                                       InputProps={{
                                          endAdornment: (
                                             <>
                                                {!isUpdate() &&
                                                   isContributorOptionSelected &&
                                                   isLegalNaturePerson && (
                                                      <Tooltip
                                                         open={isTooltipOpen}
                                                         title={
                                                            <Grid
                                                               container
                                                               wrap="nowrap"
                                                               alignItems="center"
                                                            >
                                                               <Grid
                                                                  item
                                                                  xs={3}
                                                               >
                                                                  <Warning />
                                                               </Grid>
                                                               <Grid>
                                                                  El correo
                                                                  electrónico a
                                                                  registrar,
                                                                  debe ser el de
                                                                  notificaciones
                                                                  de la cámara
                                                                  de comercio.
                                                               </Grid>
                                                            </Grid>
                                                         }
                                                         PopperProps={{
                                                            disablePortal: true
                                                         }}
                                                         arrow
                                                         placement="top-end"
                                                         classes={{
                                                            tooltip:
                                                               'tooltip_warning',
                                                            arrow: 'tooltip_arrow_warning'
                                                         }}
                                                      >
                                                         <Warning
                                                            fontSize="large"
                                                            style={{
                                                               color: 'white'
                                                            }}
                                                         />
                                                      </Tooltip>
                                                   )}
                                             </>
                                          )
                                       }}
                                    />
                                 </div>

                                 <FormControl
                                    className="mt10"
                                    style={style.recaptcha}
                                 >
                                    <Recaptcha
                                       key="form-contributor"
                                       sitekey={
                                          process.env.REACT_APP_RECAPTCHA_KEY
                                       }
                                       render="explicit"
                                       onloadCallback={() => true}
                                       verifyCallback={verifyCallback}
                                       expiredCallback={expiredCallback}
                                       hl="es-419"
                                    />
                                    {form.formState.errors?.siteKey && (
                                       <FormHelperText
                                          error={true}
                                          style={style.recaptchaError}
                                       >
                                          La validación del captcha es requerida
                                       </FormHelperText>
                                    )}
                                 </FormControl>
                                 <Grid container justifyContent="center">
                                    <Grid item xs={6} className="mb20 mt10">
                                       <Button
                                          variant="contained"
                                          color="primary"
                                          style={style.submit}
                                          onClick={form.handleSubmit(sendForm)}
                                          disabled={
                                             loading ||
                                             (isSubmitted && !isValid)
                                          }
                                          disableElevation
                                       >
                                          {loading ? (
                                             <CircularProgress size={24} />
                                          ) : (
                                             'ENVIAR'
                                          )}
                                       </Button>
                                    </Grid>
                                 </Grid>
                              </div>
                           </div>
                        ) : view.isNotRegistered ? (
                           <UserNotRegistered {...props} />
                        ) : thirdParty ? (
                           <AssociateConfirmationMessage
                              associate={thirdParty}
                              callback={() => setOpenDialog(true)}
                              {...props}
                           />
                        ) : (
                           <div />
                        )}
                     </div>
                  </Grid>
               </Grid>
               <Dialog
                  open={Boolean(contributorModalMessage)}
                  onClose={handleCloseContributorModal}
                  disableEscapeKeyDown
               >
                  <DialogContent style={{ maxWidth: 500 }}>
                     {contributorModalMessage}
                  </DialogContent>
                  <DialogActions>
                     <Button onClick={handleCloseContributorModal}>
                        Aceptar
                     </Button>
                  </DialogActions>
               </Dialog>
               <DialogUI message={openDialogError} onClose={setOpenDialogError} buttonTitle='Entendido' type='error' />
            </>
         )}
      </ThemeProvider>
   )
}

export default withRouter(RegisterForm)
